import { getBaseEmailTemplate } from './base-template';
import { plainTextToEmailHtml } from './email-plain-text-html';
import {
  getEmailPromotionalSectionHtml,
  getEmailPromotionalSectionText,
} from './email-promotional-section';

interface ShareContentData {
  contentName: string;
  roomName: string;
  accountName?: string;
  customMessage?: string;
  shareableUrl: string;
  inviterName?: string;
  recipientName?: string;
  hidePromotionalSection?: boolean;
  hideRoomName?: boolean;
}

export function getShareContentTemplate(data: ShareContentData): string {
  const baseUrl =
    process.env.NEXTAUTH_URL || process.env.NEXT_PUBLIC_APP_URL || 'https://www.smartroomsxp.com';
  const logoUrl = `${baseUrl}/sr_logo.png`;

  const recipientGreeting = data.recipientName
    ? `
      <tr>
        <td style="padding: 0 0 10px 0;">
          <p style="font-size: 17px; color: #111827; margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; line-height: 1.45;">
            Hi <strong style="font-weight: 700;">${data.recipientName}</strong>,
          </p>
        </td>
      </tr>
    `
    : `
      <tr>
        <td style="padding: 0 0 10px 0;">
          <p style="font-size: 17px; color: #111827; margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; line-height: 1.45;">
            Hello,
          </p>
        </td>
      </tr>
    `;

  const inviterMessage = data.inviterName
    ? `
      <tr>
        <td style="padding: 0 0 12px 0;">
          <p style="font-size: 16px; color: #111827; margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; line-height: 1.45;">
            <strong style="font-weight: 700;">${data.inviterName}</strong> has shared content with you.
          </p>
        </td>
      </tr>
    `
    : '';

  const customMessageHtml =
    data.customMessage?.trim() ? plainTextToEmailHtml(data.customMessage.trim()) : '';
  const customMessageBox = customMessageHtml
    ? `
    <tr>
      <td style="padding: 16px 0 0 0;">
        <p style="margin: 0 0 6px 0; padding: 0; font-size: 12px; font-weight: 600; color: #6b7280; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;">Note from the sender</p>
        <table width="100%" border="0" cellpadding="0" cellspacing="0" role="presentation" style="background-color: #f0f4ff; border-radius: 10px; border-left: 3px solid #1e3a8a;">
          <tr>
            <td style="padding: 12px 14px;">
              <p style="margin: 0; padding: 0; color: #1e3a8a; font-size: 14px; line-height: 1.5; font-style: italic; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;">
                &quot;${customMessageHtml}&quot;
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  `
    : '';

  const contentCardWithCta = `
    <tr>
      <td style="padding: 12px 0 0 0;">
        <table width="100%" border="0" cellpadding="0" cellspacing="0" role="presentation" style="background-color: #f8f9ff; border-radius: 12px; border: 1px solid #e0e7ff;">
          <tr>
            <td style="padding: 14px 16px 12px; text-align: center;">
              <p style="margin: 0; padding: 0; font-size: 11px; color: #6b7280; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; line-height: 1.4;">
                Content
              </p>
              <p style="margin: 6px 0 0 0; padding: 0; font-size: 20px; color: #111827; font-weight: 700; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; line-height: 1.25;">
                ${data.contentName}
              </p>
              ${data.hideRoomName ? '' : `
                <p style="margin: 8px 0 0 0; padding: 0; font-size: 13px; color: #6b7280; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; line-height: 1.45;">
                  From room: <strong style="color: #111827;">${data.roomName}</strong>
                </p>
              `}
            </td>
          </tr>
          <tr>
            <td style="border-top: 1px solid #e0e7ff; padding: 14px 16px 16px; text-align: center;">
              <table border="0" cellpadding="0" cellspacing="0" role="presentation" style="margin: 0 auto;">
                <tr>
                  <td align="center" style="border-radius: 10px; background-color: #1e3a8a;">
                    <!--[if mso]>
                    <v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:word" href="${data.shareableUrl}" style="height:44px;v-text-anchor:middle;width:200px;" arcsize="23%" strokecolor="#1e3a8a" fillcolor="#1e3a8a">
                      <w:anchorlock/>
                      <center style="color:#ffffff;font-family:sans-serif;font-size:15px;font-weight:600;">View Content</center>
                    </v:roundrect>
                    <![endif]-->
                    <!--[if !mso]><!-->
                    <a href="${data.shareableUrl}" target="_blank" style="display: inline-block; padding: 12px 32px; background-color: #1e3a8a; color: #ffffff; text-decoration: none; border-radius: 10px; font-weight: 600; font-size: 15px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; letter-spacing: 0.3px; line-height: 1.4; mso-hide: all;">
                      View Content
                    </a>
                    <!--<![endif]-->
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  `;

  const content = `
    <div class="header">
      <table width="100%" border="0" cellpadding="0" cellspacing="0" role="presentation" style="margin-bottom: 12px;">
        <tr>
          <td align="center" style="padding: 0 0 10px 0;">
            <a href="https://www.smartroomsxp.com" target="_blank" style="text-decoration: none;">
              <img src="${logoUrl}" alt="Smart Rooms XP" width="160" height="40" style="display: block; margin: 0 auto; height: auto; max-width: 160px;" />
            </a>
          </td>
        </tr>
      </table>
      <h1 style="margin: 0; padding: 0; font-size: 26px; font-weight: 700; letter-spacing: -0.5px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; line-height: 1.15;">Content Shared With You</h1>
    </div>
    <div class="content">
      <table width="100%" border="0" cellpadding="0" cellspacing="0" role="presentation">
        ${recipientGreeting}
        ${inviterMessage}
        ${contentCardWithCta}
        ${customMessageBox}
        <tr>
          <td align="center" style="padding: 14px 0 0 0;">
            <table width="100%" border="0" cellpadding="0" cellspacing="0" role="presentation" style="background-color: #f9fafb; border-radius: 10px; border: 1px solid #e5e7eb;">
              <tr>
                <td style="padding: 12px 14px;">
                  <p style="margin: 0; padding: 0; color: #6b7280; font-size: 13px; line-height: 1.5; text-align: center; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;">
                    Use the <strong style="color: #4b5563; font-weight: 600;">View Content</strong> button to open it. If the button doesn&apos;t work, use the link in the plain-text version of this email.
                  </p>
                </td>
              </tr>
            </table>
          </td>
        </tr>
        ${getEmailPromotionalSectionHtml(data.hidePromotionalSection, 'content_share')}
      </table>
    </div>
  `;

  return getBaseEmailTemplate(content, 'You are receiving this email because content has been shared with you.');
}

export function getShareContentText(data: ShareContentData): string {
  const inviter = data.inviterName || 'Someone';
  const greeting = data.recipientName ? `Hi ${data.recipientName},` : 'Hello,';
  const promoText = getEmailPromotionalSectionText(data.hidePromotionalSection);

  return `
Content Shared With You

${greeting}

${data.customMessage ? `"${data.customMessage}"\n\n` : ''}${inviter} has shared ${data.contentName}${data.hideRoomName ? '' : ` from the room ${data.roomName}`} with you.

View Content: ${data.shareableUrl}

Click the link above to view the content.
${promoText ? `\n\n${promoText}` : ''}

This message was sent from Smart Rooms.
  `.trim();
}
